/* eslint camelcase: 0 */
import 'isomorphic-fetch';
import get from 'lodash/get';
import ApiSearchError from './ApiSearchError';
import { API_TIMEOUT } from '../common/constants';
import {
  buildFiltersParamsString,
  buildQueryParamsString,
  applyPathParamsToUrl,
  buildQueryParamsForRelatedAds,
  buildMetricsData,
  buildRedirectParams,
} from '../common/utils';
import handleApiResponse from '../common/handleApiResponses/oldStyleResponse';
import { VERTICAL_NAMES } from '~/common/constants/common';

const END_POINTS = {
  AUTO_COMPLETE: '/autocomplete/{field}',
  SEARCH_ADS: '/search',
  // POC reduce network data
  SEARCH_ADS_MOBILE: '/search/m',
  SEARCH_RETENTION_ADS: '/retention',
  SEARCH_LANDING_ADS: '/landing',
  RELATED_ADS: '/ad/{ad_id}/related',
  AD: '/ad/{ad_id}',
  SEO_LINKS: '/seoLinks',
};

const FETCH_OPTIONS = {
  method: 'GET',
  mode: 'cors',
  timeout: API_TIMEOUT,
  headers: {
    Accept: 'application/vnd.api+json',
    'Content-Type': 'application/vnd.api+json',
  },
};

/* eslint-disable quote-props */
export const FILTERS = {
  region: true,
  city: true,
  city_area: true,
  geo_id: true,
  date_from: true,
  decrease_price: true,
  photos: true,
  parking: true,
  new_build: true,
  property_type: true,
  price_min: true,
  price_max: true,
  lat_min: true,
  lat_max: true,
  long_min: true,
  long_max: true,
  area_min: true,
  area_max: true,
  rooms_min: true,
  rooms_max: true,
  bathrooms_min: true,
  bathrooms_max: true,
  year_min: true,
  year_max: true,
  mileage_min: true,
  mileage_max: true,
  doors_min: true,
  doors_max: true,
  model: true,
  make: true,
  car_type: true,
  transmission: true,
  fuel: true,
  /* Jobs specific filters */
  salary_min: true,
  salary_max: true,
  category: true,
  company: true,
  job_type: true,
  working_hours: true,
  experience_min: true,
  telecommute: true,
};
/* eslint-enable */

class Api {
  static fetch(url, metricsData) {
    return handleApiResponse(fetch(url, FETCH_OPTIONS), ApiSearchError, metricsData)
      .then(response => response)
      .catch(error => {
        throw error;
      });
  }

  constructor(urlBase) {
    this.urlBase = urlBase;
  }

  static getParams(searchParams) {
    const origin = get(searchParams, 'origin');
    const isSemUrl = get(searchParams, 'isSemUrl');
    const isSeoUrl = get(searchParams, 'isSeoUrl');
    const boostAdsSince = get(searchParams, 'boost_ads_since');
    const sections = get(searchParams, 'sections');
    const filters = get(searchParams, 'filters');
    const sort = get(searchParams, 'sort');
    const page = get(searchParams, 'page');
    const userCampaignIds = get(searchParams, 'userCampaignIds');
    const geoId = get(searchParams, 'geoId');
    const queryParams = {
      what: get(searchParams, 'what_d'),
      dealTypeId: get(searchParams, 'type_id'),
      countryCode: get(searchParams, 'country_code'),
      device: 'mobile', // Force value
      retrieveKeyword: 'false',
    };

    if (origin) {
      queryParams.originId = origin;
    }

    if (userCampaignIds) {
      queryParams.userCampaignIds = userCampaignIds;
    }

    if (boostAdsSince) {
      queryParams.boostAdsSince = boostAdsSince;
    }

    if (isSeoUrl) {
      queryParams.retrieveKeyword = 'true';
    }

    return {
      isSemUrl,
      boostAdsSince,
      sections,
      filters,
      geoId,
      sort,
      page,
      queryParams,
    };
  }

  static computeParamsAsStringArray(params) {
    const queryParamsStr = params.queryParams ? buildQueryParamsString(params.queryParams) : '';
    const filterParamsStr = params.filters ? buildFiltersParamsString(params.filters) : '';
    const sortParamStr = params.sort ? `sort=${params.sort}` : '';
    const pageParamStr = params.page ? `page[number]=${params.page}` : '';
    const clientIdParamStr = 'clientId=pwa-mobile';

    return [queryParamsStr, filterParamsStr, sortParamStr, pageParamStr, clientIdParamStr];
  }

  /**
   * Retrieve related ads of adpage ad
   *
   * @param {Object} requestApiParams
   *
   * @returns {Object}
   *
   * @throws {ApiSearchError}
   * @memberof Api
   */
  searchRetentionAds(requestApiParams) {
    const resource = `${this.urlBase}${END_POINTS.SEARCH_RETENTION_ADS}`;

    return Api.executeSearch(requestApiParams, resource, 'retention');
  }

  /**
   * Retrieve related ads of adpage ad
   *
   * @param {Object} requestApiParams
   *
   * @returns {Object}
   *
   * @throws {ApiSearchError}
   * @memberof Api
   */
  searchLandingAds(requestApiParams) {
    const resource = `${this.urlBase}${END_POINTS.SEARCH_LANDING_ADS}`;

    return Api.executeSearch(requestApiParams, resource, 'landing');
  }

  /**
   * Retrieve related ads of adpage ad
   *
   * @param {Object} requestApiParams
   *
   * @returns {Object}
   *
   * @throws {ApiSearchError}
   * @memberof Api
   */
  searchAds(requestApiParams) {
    // POC reduce network data
    const {
      pathParams: { verticalName },
    } = requestApiParams;
    const resource = `${this.urlBase}${
      verticalName === VERTICAL_NAMES.homes ? END_POINTS.SEARCH_ADS_MOBILE : END_POINTS.SEARCH_ADS
    }`;

    return Api.executeSearch(requestApiParams, resource, 'search');
  }

  static executeSearch(requestApiParams, searchEndPoint, apiResource) {
    const { countryCode, verticalName } = requestApiParams.pathParams;

    const pathParams = {
      country_code: countryCode,
      vertical_name: verticalName,
    };

    const params = buildQueryParamsString(requestApiParams.queryParams);
    const resourceWithParams = applyPathParamsToUrl(searchEndPoint, pathParams);
    const url = `${resourceWithParams}?${params}`;

    const metricsData = buildMetricsData(verticalName, countryCode, 'search', apiResource);

    return Api.fetch(url, metricsData);
  }

  /**
   * Returns information for the given ad
   *
   * @param {Object} search Search object
   * @param {string} search.id
   * @param {string} search.countryCode
   * @param {string} search.verticalName
   *
   * @returns {Object}
   *
   * @throws {ApiSearchError}
   * @memberof Api
   */
  ad(search) {
    const resource = `${this.urlBase}${END_POINTS.AD}`;
    const { id, verticalName, countryCode, trovit, userId } = search;

    const pathParams = {
      ad_id: id,
      vertical_name: verticalName,
      country_code: countryCode,
    };

    const queryParams = {
      countryCode,
      device: 'mobile', // search.device,
      clientId: 'pwa-mobile',
      originId: trovit.origin,
      ...buildRedirectParams(trovit, userId),
      // suggester, // @todo : fill this param after recover the SPA in adpage (no adpage after redirection)
    };

    const queryParamsStr = queryParams ? buildQueryParamsString(queryParams) : '';
    const resourceWithParams = applyPathParamsToUrl(resource, pathParams);
    const url = `${resourceWithParams}?${queryParamsStr}`;

    const metricsData = buildMetricsData(
      search.verticalName,
      search.countryCode,
      'search',
      'getAd'
    );

    return Api.fetch(url, metricsData);
  }

  /**
   * Retrieve related ads of adpage ad
   *
   * @param {Object} data.ad
   * @param {string} data.countryCode
   * @param {string} data.verticalName
   * @param {Object} data.trovit
   * @param {number} data.userId
   *
   * @returns {Object}
   *
   * @throws {ApiSearchError}
   * @memberof Api
   */
  relatedAds({ ad, countryCode, verticalName, what, where, trovit, userId }) {
    const resource = `${this.urlBase}${END_POINTS.RELATED_ADS}`;
    const pathParams = {
      country_code: countryCode,
      vertical_name: verticalName,
      ad_id: ad.id,
    };

    const queryParams = buildQueryParamsForRelatedAds({
      verticalName,
      countryCode,
      ad,
      what,
      where,
      trovit,
      userId,
    });
    const queryParamsStr = buildQueryParamsString(queryParams);
    const resourceWithParams = applyPathParamsToUrl(resource, pathParams);
    const url = `${resourceWithParams}?${queryParamsStr}`;

    const metricsData = buildMetricsData(verticalName, countryCode, 'search', 'relatedAds');

    return Api.fetch(url, metricsData);
  }

  homesSeoLinks({ homePageCategory, country_code, propertyType, dealType, vertical_name }) {
    const resource = `${this.urlBase}${END_POINTS.SEO_LINKS}`;
    const pathParams = {
      country_code,
      vertical_name,
    };

    const queryParams = {
      homePageCategory,
      countryCode: country_code,
      propertyType,
      dealType,
    };

    const queryParamsStr = buildQueryParamsString(queryParams);
    const resourceWithParams = applyPathParamsToUrl(resource, pathParams);
    const url = `${resourceWithParams}?${queryParamsStr}`;

    const metricsData = buildMetricsData(
      vertical_name,
      country_code,
      'search',
      END_POINTS.SEO_LINKS.replace('/', '')
    );

    return Api.fetch(url, metricsData);
  }

  carsSeoLinks({ country_code }) {
    const resource = `${this.urlBase}${END_POINTS.SEO_LINKS}`;
    const pathParams = {
      country_code,
      vertical_name: 'cars',
    };

    const queryParams = {
      countryCode: country_code,
    };

    const queryParamsStr = buildQueryParamsString(queryParams);
    const resourceWithParams = applyPathParamsToUrl(resource, pathParams);
    const url = `${resourceWithParams}?${queryParamsStr}`;

    const metricsData = buildMetricsData(
      pathParams.vertical_name,
      country_code,
      'search',
      END_POINTS.SEO_LINKS.replace('/', '')
    );

    return Api.fetch(url, metricsData);
  }

  jobsSeoLinks({ country_code }) {
    const resource = `${this.urlBase}${END_POINTS.SEO_LINKS}`;
    const pathParams = {
      country_code,
      vertical_name: 'jobs',
    };

    const queryParams = {
      countryCode: country_code,
    };

    const queryParamsStr = buildQueryParamsString(queryParams);
    const resourceWithParams = applyPathParamsToUrl(resource, pathParams);
    const url = `${resourceWithParams}?${queryParamsStr}`;

    const metricsData = buildMetricsData(
      pathParams.vertical_name,
      country_code,
      'search',
      END_POINTS.SEO_LINKS.replace('/', '')
    );

    return Api.fetch(url, metricsData);
  }

  /**
   * Request suggestions given the specified what
   *
   * @param {Object} data
   * @param {Object} data.search
   * @param {string} data.search.what
   * @param {string} data.search.where
   * @param {string} data.country_code
   * @param {string} data.vertical_name
   *
   * @returns {Object}
   *
   * @throws {ApiKeywordsError}
   * @memberof Api
   */
  autocomplete({ search, country_code, vertical_name }) {
    const urlBase = `${this.urlBase}${END_POINTS.AUTO_COMPLETE}`;
    const resource = applyPathParamsToUrl(urlBase, {
      country_code,
      vertical_name,
      field: `${Object.keys(search)}`,
    });

    const queryParams = {
      term: search.where || search.what,
      countryCode: country_code,
    };

    const queryParamsStr = buildQueryParamsString(queryParams);
    const url = `${resource}?${queryParamsStr}`;

    return Api.fetch(url);
  }
}

export default Api;



// WEBPACK FOOTER //
// ./app/sdk/search/Api.js